import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Send, Building2, Calendar, Plus, Trash2, Package, Loader2, AlertCircle, X, Search } from 'lucide-react';
import { adminQuotesAPI } from '../../services/api';
import { formatMoney } from '../../utils/format';

// Simple debounce utility
function useDebounce(callback, delay) {
  const timeoutRef = useRef(null);

  const debouncedFn = useCallback((...args) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      callback(...args);
    }, delay);
  }, [callback, delay]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return debouncedFn;
}

export default function AdminQuoteEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';

  // Form state
  const [formData, setFormData] = useState({
    companyId: null,
    companyName: '',
    contactId: null,
    contactName: '',
    contactEmail: '',
    quoteName: '',
    description: '',
    notes: '',
    // Local-TZ "today" / "today + 30 days" so the form defaults match the
    // user's perception of the calendar.
    quoteDate: new Date().toLocaleDateString('en-CA'),
    validUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString('en-CA'),
    termsAndConditions: '',
    stageId: '',
    depositType: 'fixed', // 'fixed' | 'percentTotal' | 'percentLineItem'
    depositValue: '',
    depositLineItemIndex: '',
    paymentTerms: '',
    shippingMethod: '',
    taxRate: '',
    taxRateSource: '' // 'cw_company' | 'manual' | ''
  });

  const [stages, setStages] = useState([]);

  useEffect(() => {
    adminQuotesAPI.getStages()
      .then(res => setStages(res.data?.stages || []))
      .catch(err => console.warn('Failed to load CW stages:', err.message));
  }, []);

  const [lineItems, setLineItems] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Company search state
  const [companySearch, setCompanySearch] = useState('');
  const [companyResults, setCompanyResults] = useState([]);
  const [searchingCompanies, setSearchingCompanies] = useState(false);
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);

  // Contacts state
  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);

  // Product search state (per line item)
  const [activeProductSearch, setActiveProductSearch] = useState(null);
  const [productSearch, setProductSearch] = useState('');
  const [productResults, setProductResults] = useState([]);
  const [searchingProducts, setSearchingProducts] = useState(false);
  const productDropdownRef = useRef(null);

  // Load existing quote
  useEffect(() => {
    if (!isNew) {
      fetchQuote();
    }
  }, [id]);

  const fetchQuote = async () => {
    try {
      setLoading(true);
      const response = await adminQuotesAPI.get(id);
      const { quote, lineItems: items } = response.data;

      setFormData({
        companyId: quote.companyId,
        companyName: quote.companyName,
        contactId: quote.contactId,
        contactName: quote.contactName,
        contactEmail: quote.contactEmail,
        quoteName: quote.quoteName,
        description: quote.description || '',
        notes: quote.notes || '',
        quoteDate: quote.quoteDate?.split('T')[0] || '',
        validUntil: quote.validUntil?.split('T')[0] || '',
        termsAndConditions: quote.termsAndConditions || ''
      });

      setLineItems(items.map(item => ({
        id: item.id,
        productName: item.product_name,
        productCode: item.product_code || '',
        description: item.description || '',
        quantity: item.quantity,
        unitPrice: parseFloat(item.unit_price),
        itemType: item.item_type || 'product',
        isOptional: item.is_optional || false,
        packageGroup: item.package_group || '',
        packageOption: item.package_option || '',
        isDefaultSelection: item.is_default_selection || false,
        taxable: item.taxable || false,
        taxRate: item.tax_rate || ''
      })));

      // Load contacts for the company
      if (quote.companyId) {
        fetchContacts(quote.companyId);
      }
    } catch (err) {
      console.error('Error fetching quote:', err);
      setError(err.response?.data?.error || 'Failed to load quote');
    } finally {
      setLoading(false);
    }
  };

  // Debounced company search
  const doCompanySearch = useCallback(async (query) => {
    if (query.length < 2) {
      setCompanyResults([]);
      setSearchingCompanies(false);
      return;
    }

    try {
      setSearchingCompanies(true);
      const response = await adminQuotesAPI.searchCompanies(query);
      setCompanyResults(response.data.companies || []);
    } catch (err) {
      console.error('Error searching companies:', err);
    } finally {
      setSearchingCompanies(false);
    }
  }, []);

  const searchCompanies = useDebounce(doCompanySearch, 300);

  const fetchContacts = async (companyId) => {
    try {
      setLoadingContacts(true);
      const response = await adminQuotesAPI.getCompanyContacts(companyId);
      setContacts(response.data.contacts || []);
    } catch (err) {
      console.error('Error fetching contacts:', err);
    } finally {
      setLoadingContacts(false);
    }
  };

  // Product search
  const doProductSearch = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setProductResults([]);
      setSearchingProducts(false);
      return;
    }

    try {
      setSearchingProducts(true);
      const response = await adminQuotesAPI.searchProducts(query);
      setProductResults(response.data.products || []);
    } catch (err) {
      console.error('Error searching products:', err);
      setProductResults([]);
    } finally {
      setSearchingProducts(false);
    }
  }, []);

  const searchProducts = useDebounce(doProductSearch, 300);

  const selectProduct = (index, product) => {
    updateLineItem(index, 'productCode', product.identifier || '');
    updateLineItem(index, 'productName', product.description || '');
    updateLineItem(index, 'unitPrice', product.price || 0);
    updateLineItem(index, 'taxable', product.taxable || false);
    // Close search
    setActiveProductSearch(null);
    setProductSearch('');
    setProductResults([]);
  };

  // Close product dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (productDropdownRef.current && !productDropdownRef.current.contains(event.target)) {
        setActiveProductSearch(null);
        setProductResults([]);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCompanySelect = (company) => {
    setFormData(prev => ({
      ...prev,
      companyId: company.id,
      companyName: company.name,
      contactId: null,
      contactName: '',
      contactEmail: ''
    }));
    setCompanySearch('');
    setCompanyResults([]);
    setShowCompanyDropdown(false);
    fetchContacts(company.id);

    // Auto-fetch tax rate from CW company tax code (unless user already typed a manual override)
    adminQuotesAPI.getCompanyTaxRate(company.id)
      .then(res => {
        const rate = res.data?.taxRate;
        setFormData(prev => {
          if (prev.taxRateSource === 'manual') return prev; // don't overwrite manual entry
          return { ...prev, taxRate: rate > 0 ? String(rate) : '', taxRateSource: rate > 0 ? 'cw_company' : '' };
        });
      })
      .catch(() => { /* non-fatal */ });
  };

  const handleContactSelect = (contactId) => {
    const contact = contacts.find(c => c.id === parseInt(contactId));
    if (contact) {
      setFormData(prev => ({
        ...prev,
        contactId: contact.id,
        contactName: `${contact.firstName || ''} ${contact.lastName || ''}`.trim(),
        contactEmail: contact.email || ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        contactId: null,
        contactName: '',
        contactEmail: ''
      }));
    }
  };

  const addLineItem = () => {
    setLineItems(prev => [...prev, {
      id: `new-${Date.now()}`,
      productName: '',
      productCode: '',
      description: '',
      quantity: 1,
      unitPrice: 0,
      itemType: 'product',
      isOptional: false,
      packageGroup: '',
      packageOption: '',
      isDefaultSelection: false,
      taxable: false,
      taxRate: ''
    }]);
  };

  const updateLineItem = (index, field, value) => {
    setLineItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const removeLineItem = (index) => {
    setLineItems(prev => prev.filter((_, i) => i !== index));
  };

  const calculateSubtotal = () => {
    return lineItems.reduce((sum, item) => {
      return sum + (parseFloat(item.unitPrice) || 0) * (parseInt(item.quantity) || 0);
    }, 0);
  };

  const calculateTax = () => {
    const rate = parseFloat(formData.taxRate) || 0;
    if (rate <= 0) return 0;
    const taxable = lineItems.reduce((sum, item) => {
      if (!item.taxable) return sum;
      return sum + (parseFloat(item.unitPrice) || 0) * (parseInt(item.quantity) || 0);
    }, 0);
    return Math.round(taxable * (rate / 100) * 100) / 100;
  };

  const calculateTotal = () => calculateSubtotal() + calculateTax();

  // Compute the deposit dollar amount based on the chosen rule.
  // 'percentTotal' uses the tax-inclusive grand total.
  const computeDepositAmount = () => {
    const val = parseFloat(formData.depositValue);
    if (!val || isNaN(val) || val <= 0) return 0;
    if (formData.depositType === 'fixed') {
      return Math.round(val * 100) / 100;
    }
    if (formData.depositType === 'percentTotal') {
      return Math.round(calculateTotal() * (val / 100) * 100) / 100;
    }
    if (formData.depositType === 'percentLineItem') {
      const idx = parseInt(formData.depositLineItemIndex);
      const item = lineItems[idx];
      if (!item) return 0;
      const lineTotal = (parseFloat(item.unitPrice) || 0) * (parseInt(item.quantity) || 0);
      return Math.round(lineTotal * (val / 100) * 100) / 100;
    }
    return 0;
  };

  const formatCurrency = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const handleSave = async (publish = false) => {
    try {
      setSaving(true);
      setError(null);

      if (!formData.companyId || !formData.quoteName) {
        setError('Company and quote name are required');
        return;
      }

      // Compute the actual deposit dollar amount from the chosen rule
      const computedDeposit = computeDepositAmount();

      // Build payload — strip frontend-only rule fields, send the final dollar amount as depositAmount
      const { depositType, depositValue, depositLineItemIndex, taxRateSource, ...rest } = formData;
      const payload = {
        ...rest,
        taxRate: formData.taxRate ? parseFloat(formData.taxRate) : null,
        depositAmount: computedDeposit > 0 ? computedDeposit : '',
        lineItems: lineItems.map(item => ({
          productName: item.productName,
          productCode: item.productCode,
          description: item.description,
          quantity: parseInt(item.quantity) || 1,
          unitPrice: parseFloat(item.unitPrice) || 0,
          itemType: item.itemType,
          isOptional: item.isOptional,
          packageGroup: item.packageGroup,
          packageOption: item.packageOption,
          isDefaultSelection: item.isDefaultSelection,
          taxable: item.taxable,
          taxRate: item.taxRate ? parseFloat(item.taxRate) : null
        }))
      };

      let quoteId;

      if (isNew) {
        const response = await adminQuotesAPI.create(payload);
        quoteId = response.data.quote.id;
      } else {
        await adminQuotesAPI.update(id, payload);
        quoteId = id;
      }

      if (publish) {
        await adminQuotesAPI.publish(quoteId);
      }

      navigate(`/admin/quotes/${quoteId}/edit`);
    } catch (err) {
      console.error('Error saving quote:', err);
      // Validation errors come back as { errors: [{ msg, param, path }] }
      const validationErrors = err.response?.data?.errors;
      if (Array.isArray(validationErrors) && validationErrors.length > 0) {
        setError(validationErrors.map(e => `${e.path || e.param}: ${e.msg}`).join('; '));
      } else {
        setError(err.response?.data?.error || err.response?.data?.message || 'Failed to save quote');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/admin/quotes')}
          className="flex items-center gap-2 text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Quotes
        </button>
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">
            {isNew ? 'Create New Quote' : 'Edit Quote'}
          </h1>
          <div className="flex items-center gap-3">
            <button
              onClick={() => handleSave(false)}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700 font-medium disabled:opacity-50 dark:text-white"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save Draft
            </button>
            <button
              onClick={() => handleSave(true)}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 font-medium disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Save & Publish
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 dark:text-red-400" />
          <span className="text-red-700 dark:text-red-300">{error}</span>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Company & Contact */}
          <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6">
            <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-primary-500" />
              Customer Information
            </h2>

            <div className="space-y-4">
              {/* Company Search */}
              <div className="relative">
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Company *
                </label>
                {formData.companyId ? (
                  <div className="flex items-center justify-between p-3 bg-neutral-50 dark:bg-neutral-700/50 rounded-lg border border-neutral-200 dark:border-neutral-600">
                    <div>
                      <div className="font-medium text-neutral-900 dark:text-white">{formData.companyName}</div>
                      <div className="text-sm text-neutral-500 dark:text-neutral-400">ID: {formData.companyId}</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          companyId: null,
                          companyName: '',
                          contactId: null,
                          contactName: '',
                          contactEmail: ''
                        }));
                        setContacts([]);
                      }}
                      className="p-1 text-neutral-500 hover:text-red-500 dark:text-neutral-400 dark:hover:text-red-400"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
                    <input
                      type="text"
                      value={companySearch}
                      onChange={(e) => {
                        setCompanySearch(e.target.value);
                        searchCompanies(e.target.value);
                        setShowCompanyDropdown(true);
                      }}
                      onFocus={() => setShowCompanyDropdown(true)}
                      placeholder="Search for a company..."
                      className="w-full pl-10 pr-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-neutral-700 dark:text-white"
                    />
                    {showCompanyDropdown && (companyResults.length > 0 || searchingCompanies) && (
                      <div className="absolute z-10 w-full mt-1 bg-white dark:bg-neutral-700 border border-neutral-200 dark:border-neutral-600 rounded-lg shadow-lg max-h-60 overflow-auto">
                        {searchingCompanies ? (
                          <div className="p-3 text-center text-neutral-500 dark:text-neutral-400">
                            <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                          </div>
                        ) : (
                          companyResults.map(company => (
                            <button
                              key={company.id}
                              type="button"
                              onClick={() => handleCompanySelect(company)}
                              className="w-full px-4 py-2 text-left hover:bg-neutral-50 dark:hover:bg-neutral-600 border-b border-neutral-100 dark:border-neutral-600 last:border-0"
                            >
                              <div className="font-medium text-neutral-900 dark:text-white">{company.name}</div>
                              <div className="text-sm text-neutral-500 dark:text-neutral-400">
                                {company.identifier} {company.city && `• ${company.city}, ${company.state}`}
                              </div>
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Contact Select */}
              {formData.companyId && (
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                    Contact
                  </label>
                  {loadingContacts ? (
                    <div className="p-3 text-center text-neutral-500 dark:text-neutral-400">
                      <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                    </div>
                  ) : (
                    <select
                      value={formData.contactId || ''}
                      onChange={(e) => handleContactSelect(e.target.value)}
                      className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-neutral-700 dark:text-white"
                    >
                      <option value="">Select a contact (optional)</option>
                      {contacts.map(contact => (
                        <option key={contact.id} value={contact.id}>
                          {contact.firstName} {contact.lastName} {contact.email && `(${contact.email})`}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Quote Details */}
          <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6">
            <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-primary-500" />
              Quote Details
            </h2>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Quote Name *
                </label>
                <input
                  type="text"
                  value={formData.quoteName}
                  onChange={(e) => setFormData(prev => ({ ...prev, quoteName: e.target.value }))}
                  placeholder="e.g., Network Infrastructure Upgrade"
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-neutral-700 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Quote Date
                </label>
                <input
                  type="date"
                  value={formData.quoteDate}
                  onChange={(e) => setFormData(prev => ({ ...prev, quoteDate: e.target.value }))}
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-neutral-700 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Valid Until
                </label>
                <input
                  type="date"
                  value={formData.validUntil}
                  onChange={(e) => setFormData(prev => ({ ...prev, validUntil: e.target.value }))}
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-neutral-700 dark:text-white"
                />
                <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">Also sets the Quote Expiration custom field on the CW opportunity.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Stage
                </label>
                <select
                  value={formData.stageId || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, stageId: e.target.value ? parseInt(e.target.value) : '' }))}
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-neutral-700 dark:text-white"
                >
                  <option value="">Use CW default</option>
                  {stages.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>

              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Deposit
                </label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <select
                    value={formData.depositType}
                    onChange={(e) => setFormData(prev => ({ ...prev, depositType: e.target.value, depositLineItemIndex: e.target.value === 'percentLineItem' ? prev.depositLineItemIndex : '' }))}
                    className="px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-neutral-700 dark:text-white sm:w-48"
                  >
                    <option value="fixed">Fixed amount ($)</option>
                    <option value="percentTotal">% of total</option>
                    <option value="percentLineItem">% of line item</option>
                  </select>
                  <div className="relative flex-1">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max={formData.depositType !== 'fixed' ? '100' : undefined}
                      value={formData.depositValue}
                      onChange={(e) => setFormData(prev => ({ ...prev, depositValue: e.target.value }))}
                      placeholder={formData.depositType === 'fixed' ? '0.00' : '0'}
                      className="w-full pl-7 pr-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-neutral-700 dark:text-white"
                    />
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500 dark:text-neutral-400">
                      {formData.depositType === 'fixed' ? '$' : '%'}
                    </span>
                  </div>
                  {formData.depositType === 'percentLineItem' && (
                    <select
                      value={formData.depositLineItemIndex}
                      onChange={(e) => setFormData(prev => ({ ...prev, depositLineItemIndex: e.target.value }))}
                      className="px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-neutral-700 dark:text-white sm:flex-1"
                    >
                      <option value="">Select line item...</option>
                      {lineItems.map((item, idx) => (
                        <option key={idx} value={idx}>
                          {item.productName || `Line ${idx + 1}`} ({formatCurrency((parseFloat(item.unitPrice) || 0) * (parseInt(item.quantity) || 0))})
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                {parseFloat(formData.depositValue) > 0 && (
                  <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                    Calculated deposit: <span className="font-semibold text-neutral-700 dark:text-neutral-300">{formatCurrency(computeDepositAmount())}</span>
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Payment Terms
                </label>
                <input
                  type="text"
                  value={formData.paymentTerms}
                  onChange={(e) => setFormData(prev => ({ ...prev, paymentTerms: e.target.value }))}
                  placeholder="e.g., Net 30, Due on Receipt"
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-neutral-700 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Shipping Method
                </label>
                <input
                  type="text"
                  value={formData.shippingMethod}
                  onChange={(e) => setFormData(prev => ({ ...prev, shippingMethod: e.target.value }))}
                  placeholder="e.g., Ground, Overnight"
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-neutral-700 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Tax Rate (%)
                </label>
                <div className="relative">
                  <input
                    type="number"
                    step="0.0001"
                    min="0"
                    max="100"
                    value={formData.taxRate}
                    onChange={(e) => setFormData(prev => ({ ...prev, taxRate: e.target.value, taxRateSource: 'manual' }))}
                    placeholder="0"
                    className="w-full px-3 py-2 pr-10 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-neutral-700 dark:text-white"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 dark:text-neutral-400">%</span>
                </div>
                <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                  {formData.taxRateSource === 'cw_company'
                    ? 'Auto-filled from ConnectWise company tax code. Type to override.'
                    : formData.taxRateSource === 'manual'
                      ? 'Manual override.'
                      : 'Applies to any line item marked Taxable.'}
                </p>
              </div>

              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  placeholder="Brief description of the quote..."
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-neutral-700 dark:text-white"
                />
              </div>
            </div>
          </div>

          {/* Line Items */}
          <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-neutral-900 dark:text-white flex items-center gap-2">
                <Package className="w-5 h-5 text-primary-500" />
                Line Items
              </h2>
              <button
                type="button"
                onClick={addLineItem}
                className="flex items-center gap-2 px-3 py-1.5 text-sm bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 rounded-lg hover:bg-primary-100 dark:hover:bg-primary-900/50"
              >
                <Plus className="w-4 h-4" />
                Add Item
              </button>
            </div>

            {lineItems.length === 0 ? (
              <div className="text-center py-8 text-neutral-500 dark:text-neutral-400">
                <Package className="w-12 h-12 mx-auto mb-3 text-neutral-300 dark:text-neutral-600" />
                <p>No line items yet</p>
                <button
                  type="button"
                  onClick={addLineItem}
                  className="mt-3 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
                >
                  Add your first item
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {lineItems.map((item, index) => (
                  <div key={item.id} className="p-4 bg-neutral-50 dark:bg-neutral-700/50 rounded-lg border border-neutral-200 dark:border-neutral-600">
                    <div className="flex items-start gap-4">
                      <div className="flex-1 grid sm:grid-cols-6 gap-3">
                        <div className="sm:col-span-3 relative" ref={activeProductSearch === index ? productDropdownRef : null}>
                          <label className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">Product *</label>
                          <div className="relative">
                            <input
                              type="text"
                              value={activeProductSearch === index ? productSearch : (item.productName || '')}
                              onChange={(e) => {
                                if (activeProductSearch !== index) {
                                  setActiveProductSearch(index);
                                }
                                setProductSearch(e.target.value);
                                searchProducts(e.target.value);
                                // Also update the product name so it persists if user types custom value
                                updateLineItem(index, 'productName', e.target.value);
                              }}
                              onFocus={() => {
                                setActiveProductSearch(index);
                                setProductSearch(item.productName || '');
                              }}
                              placeholder="Search CW products..."
                              className="w-full px-2 py-1.5 text-sm border border-neutral-300 dark:border-neutral-600 rounded focus:ring-2 focus:ring-primary-500 pr-8 bg-white dark:bg-neutral-700 dark:text-white"
                            />
                            {searchingProducts && activeProductSearch === index && (
                              <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-neutral-400" />
                            )}
                          </div>

                          {/* Product Search Results Dropdown */}
                          {activeProductSearch === index && productResults.length > 0 && (
                            <div className="absolute z-20 mt-1 w-full bg-white dark:bg-neutral-700 border border-neutral-200 dark:border-neutral-600 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                              {productResults.map((product) => (
                                <button
                                  key={product.id}
                                  type="button"
                                  onClick={() => selectProduct(index, product)}
                                  className="w-full px-3 py-2 text-left hover:bg-neutral-50 dark:hover:bg-neutral-600 border-b border-neutral-100 dark:border-neutral-600 last:border-0"
                                >
                                  <div className="flex items-center justify-between">
                                    <span className="font-medium text-sm text-neutral-900 dark:text-white">{product.identifier}</span>
                                    <span className="text-xs text-primary-600 dark:text-primary-400">${product.price?.toFixed(2) || '0.00'}</span>
                                  </div>
                                  <div className="text-xs text-neutral-500 dark:text-neutral-400 truncate">{product.description}</div>
                                </button>
                              ))}
                            </div>
                          )}

                          {/* No results message */}
                          {activeProductSearch === index && productSearch.length >= 2 &&
                           !searchingProducts && productResults.length === 0 && (
                            <div className="absolute z-20 mt-1 w-full bg-white dark:bg-neutral-700 border border-neutral-200 dark:border-neutral-600 rounded-lg shadow-lg p-2 text-xs text-neutral-500 dark:text-neutral-400">
                              No products found. Type a custom name.
                            </div>
                          )}
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">Code</label>
                          <input
                            type="text"
                            value={item.productCode}
                            onChange={(e) => updateLineItem(index, 'productCode', e.target.value)}
                            placeholder="SKU"
                            className="w-full px-2 py-1.5 text-sm border border-neutral-300 dark:border-neutral-600 rounded focus:ring-2 focus:ring-primary-500 bg-white dark:bg-neutral-700 dark:text-white"
                            readOnly={false}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">Qty</label>
                          <input
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(e) => updateLineItem(index, 'quantity', e.target.value)}
                            className="w-full px-2 py-1.5 text-sm border border-neutral-300 dark:border-neutral-600 rounded focus:ring-2 focus:ring-primary-500 bg-white dark:bg-neutral-700 dark:text-white"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">Unit Price</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={item.unitPrice}
                            onChange={(e) => updateLineItem(index, 'unitPrice', e.target.value)}
                            className="w-full px-2 py-1.5 text-sm border border-neutral-300 dark:border-neutral-600 rounded focus:ring-2 focus:ring-primary-500 bg-white dark:bg-neutral-700 dark:text-white"
                          />
                        </div>
                        <div className="sm:col-span-6">
                          <label className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">Description</label>
                          <input
                            type="text"
                            value={item.description}
                            onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                            placeholder="Item description"
                            className="w-full px-2 py-1.5 text-sm border border-neutral-300 dark:border-neutral-600 rounded focus:ring-2 focus:ring-primary-500 bg-white dark:bg-neutral-700 dark:text-white"
                          />
                        </div>
                        <div className="sm:col-span-6 flex flex-wrap gap-4">
                          <label className="flex items-center gap-2 text-sm dark:text-neutral-300">
                            <input
                              type="checkbox"
                              checked={item.isOptional}
                              onChange={(e) => updateLineItem(index, 'isOptional', e.target.checked)}
                              className="rounded border-neutral-300 dark:border-neutral-600 text-primary-500 focus:ring-primary-500 dark:bg-neutral-700"
                            />
                            Optional
                          </label>
                          <label className="flex items-center gap-2 text-sm dark:text-neutral-300">
                            <input
                              type="checkbox"
                              checked={item.taxable}
                              onChange={(e) => updateLineItem(index, 'taxable', e.target.checked)}
                              className="rounded border-neutral-300 dark:border-neutral-600 text-primary-500 focus:ring-primary-500 dark:bg-neutral-700"
                            />
                            Taxable
                          </label>
                          {item.isOptional && (
                            <>
                              <input
                                type="text"
                                value={item.packageGroup}
                                onChange={(e) => updateLineItem(index, 'packageGroup', e.target.value)}
                                placeholder="Package Group"
                                className="px-2 py-1 text-sm border border-neutral-300 dark:border-neutral-600 rounded w-32 bg-white dark:bg-neutral-700 dark:text-white"
                              />
                              <input
                                type="text"
                                value={item.packageOption}
                                onChange={(e) => updateLineItem(index, 'packageOption', e.target.value)}
                                placeholder="Option Name"
                                className="px-2 py-1 text-sm border border-neutral-300 dark:border-neutral-600 rounded w-32 bg-white dark:bg-neutral-700 dark:text-white"
                              />
                            </>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <div className="text-right">
                          <div className="text-xs text-neutral-500 dark:text-neutral-400">Total</div>
                          <div className="font-semibold text-neutral-900 dark:text-white">
                            ${((parseFloat(item.unitPrice) || 0) * (parseInt(item.quantity) || 0)).toFixed(2)}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeLineItem(index)}
                          className="p-1 text-neutral-400 hover:text-red-500 dark:hover:text-red-400"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Terms */}
          <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6">
            <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">Terms & Conditions</h2>
            <textarea
              value={formData.termsAndConditions}
              onChange={(e) => setFormData(prev => ({ ...prev, termsAndConditions: e.target.value }))}
              rows={6}
              placeholder="Enter terms and conditions..."
              className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-neutral-700 dark:text-white"
            />
          </div>
        </div>

        {/* Sidebar Summary */}
        <div className="lg:col-span-1">
          <div className="sticky top-4 bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6">
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">Quote Summary</h3>

            <div className="space-y-3 mb-6">
              <div className="flex justify-between text-sm">
                <span className="text-neutral-600 dark:text-neutral-400">Items</span>
                <span className="font-medium text-neutral-900 dark:text-white">{lineItems.length}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-neutral-600 dark:text-neutral-400">Standard Items</span>
                <span className="font-medium text-neutral-900 dark:text-white">
                  {lineItems.filter(i => !i.isOptional).length}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-neutral-600 dark:text-neutral-400">Optional Items</span>
                <span className="font-medium text-neutral-900 dark:text-white">
                  {lineItems.filter(i => i.isOptional).length}
                </span>
              </div>
              <div className="pt-3 border-t border-neutral-200 dark:border-neutral-700 space-y-1.5">
                <div className="flex justify-between text-sm">
                  <span className="text-neutral-600 dark:text-neutral-400">Subtotal</span>
                  <span className="font-medium text-neutral-900 dark:text-white">
                    {formatMoney(calculateSubtotal())}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-neutral-600 dark:text-neutral-400">
                    Tax{parseFloat(formData.taxRate) > 0 ? ` (${formData.taxRate}%)` : ''}
                  </span>
                  <span className="font-medium text-neutral-900 dark:text-white">
                    {formatMoney(calculateTax())}
                  </span>
                </div>
                <div className="flex justify-between pt-2 border-t border-neutral-200 dark:border-neutral-700">
                  <span className="font-medium text-neutral-900 dark:text-white">Total</span>
                  <span className="text-xl font-bold text-primary-600 dark:text-primary-400">
                    {formatMoney(calculateTotal())}
                  </span>
                </div>
                <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                  Includes all standard items. Optional items priced separately.
                </p>
              </div>
            </div>

            <div className="space-y-3">
              <button
                onClick={() => handleSave(false)}
                disabled={saving}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700 font-medium disabled:opacity-50 dark:text-white"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save as Draft
              </button>
              <button
                onClick={() => handleSave(true)}
                disabled={saving || !formData.companyId || !formData.quoteName}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 font-medium disabled:opacity-50"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Publish Quote
              </button>
            </div>

            {(!formData.companyId || !formData.quoteName) && (
              <p className="text-xs text-amber-600 dark:text-amber-400 mt-3">
                Select a company and enter a quote name to publish.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
